import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "@/components/common/Link";
import { ArrowRight, BookOpen } from "lucide-react";
function PostsGridConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => <Card key={i} data-index={i} className="rounded-3xl border border-border bg-card p-6 shadow-xs flex flex-col justify-between">
            <div className="space-y-4">
              <Skeleton className="w-full aspect-video rounded-2xl" />
              <div className="space-y-2">
                <Skeleton className="h-6 w-3/4 rounded-md" />
                <Skeleton className="h-4 w-full rounded-md" />
                <Skeleton className="h-4 w-5/6 rounded-md" />
              </div>
            </div>
            <div className="pt-6">
              <Skeleton className="h-9 w-28 rounded-full" />
            </div>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="rounded-3xl border border-border bg-card p-8 text-center max-w-md mx-auto">
        <p className="text-sm text-muted-foreground mb-4">
          Aktuelle Artikel können derzeit nicht geladen werden.
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Erneut versuchen
        </Button>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="rounded-3xl border border-border bg-card p-8 text-center max-w-md mx-auto">
        <p className="text-sm text-muted-foreground">
          Noch keine Reiseartikel veröffentlicht. Schauen Sie bald wieder vorbei für Ratgeber und Tipps.
        </p>
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {posts.map((post, i) => {
      // Strip HTML tags for clean skim-friendly excerpt preview
      const rawExcerpt = post.excerpt ? post.excerpt.replace(/<[^>]*>?/gm, "").trim() : "";
      const cleanExcerpt = rawExcerpt.length > 120 ? `${rawExcerpt.slice(0, 117)}...` : rawExcerpt;
      const postLink = post.link || "#";
      return <Card key={post.id} data-index={i} className="group rounded-3xl border border-border bg-card p-6 shadow-xs hover:border-primary/80 transition-all duration-300 flex flex-col justify-between">
            <div>
              {post.featuredImage ? <div className="w-full aspect-video rounded-2xl overflow-hidden mb-5 bg-muted">
                  <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div> : <div className="w-full aspect-video rounded-2xl mb-5 bg-muted/60 flex items-center justify-center text-muted-foreground">
                  <BookOpen className="size-8 stroke-[1.5] opacity-40" />
                </div>}

              <CardContent className="p-0">
                <h3 className="text-lg font-bold text-card-foreground group-hover:text-primary transition-colors duration-200 line-clamp-2 leading-snug">
                  {post.title}
                </h3>
                {cleanExcerpt && <p className="mt-2.5 text-sm text-muted-foreground line-clamp-3 leading-relaxed">
                    {cleanExcerpt}
                  </p>}
              </CardContent>
            </div>

            <div className="pt-6 mt-auto">
              <Button asChild variant="secondary" size="sm" className="rounded-full px-4 text-xs font-semibold group/btn hover:bg-primary hover:text-primary-foreground transition-colors">
                <Link to={postLink}>
                  <span>Weiterlesen</span>
                  <ArrowRight className="size-3.5 transition-transform duration-200 group-hover/btn:translate-x-0.5" />
                </Link>
              </Button>
            </div>
          </Card>;
    })}
    </div>;
}
export default function TravelTips() {
  const queryParams = {
    post_type: "post",
    per_page: 6,
    orderby: "date",
    order: "desc",
    category_name: "artikel",
    category__not_in: [1]
  };
  const wp_query = new WP_Query(queryParams);
  return <section id="traveltipsandtransferguides" data-nav="light" className="bg-background text-foreground py-20 px-4 sm:px-6 lg:px-8 border-t border-border/40">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-12 lg:mb-14">
          <Badge variant="outline" className="rounded-full px-3.5 py-1 mb-4 text-xs font-semibold tracking-wide uppercase border-border/80 bg-background/80">
            <span className="size-2 rounded-full bg-primary inline-block mr-1.5" />
            Einblicke &amp; Tipps
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-foreground tracking-tight">
            Reisetipps &amp; Transfer-Ratgeber
          </h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed">
            Wichtige Streckentipps, Orientierung am Flughafen und Empfehlungen für Transfers in Ägypten.
          </p>
        </div>

        {/* Dynamic WordPress Posts Grid */}
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 6,
        orderby: "date",
        order: "desc",
        status: ["publish"],
        categories_exclude: [1]
      }}>
          <PostsGridConsumer />
        </PostsProvider>

        {/* Centered CTA */}
        <div className="mt-12 text-center">
          <Button asChild size="lg" className="rounded-full px-8 bg-primary text-primary-foreground font-semibold hover:bg-primary/90 shadow-sm">
            <Link to="/TravelGuidesDePage">
              <span>Alle Artikel ansehen</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}